import {
  changeUserEmail,
  getAreaInfoById,
  getPaymentInfoById,
  getPaymentState,
  resetPaymentInfo,
  fetchPaymentAsync,
} from '@/store/slices/stagesSlice/stagesSlice'
import { Button, Container, Text, Time, Title } from '@/components'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/store'
import { useRouter } from 'next/router'
import { KeyboardEvent, useCallback, useEffect, useLayoutEffect, useState } from 'react'
import CarNumberLabel from './CarNumberLabel'
import styles from './PaymentPage.module.scss'
import getLinkHref from '@/helpers/getLinkHref'
import { getFormattedCarNumber } from '@/helpers/getFormattedCarNumber'
import { useAreaStyles } from '@/helpers/hooks/useAreaStyles'
import EmailInput from '@/components/ui/EmailInput'
import NoPaymentRequired from './NoPaymentRequired'
import { supportPhoneLink } from '@/constants';
import StageCost from './StageCost'

export default function PaymentPage({ stageId, areaId }: { stageId: string; areaId: string }) {
  useAreaStyles(areaId)
  const reduxState = useSelector<RootState, RootState>(state => state)
  const stageCost = reduxState.stages.stageCost
  const dispatch = useDispatch()
  const router = useRouter()

  const { data: areaInfo } = getAreaInfoById(reduxState, areaId)
  const { data } = getPaymentInfoById(reduxState, stageId)
  const { loading } = getPaymentState(reduxState)
  
  const time = data?.range ?? 0
  const paidUntil = data?.paid_until ?? null
  const isNoPaymentRequired = stageCost?.data?.total_amount === 0 && stageCost?.data?.fines_amount === 0
  const isRenterDiscountAvailable = data?.area.is_renter_discount_available

  const [carNumber, setCarNumber] = useState('')
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')

  const onInputChange = useCallback((email: string) => {
    setEmailError('')
    setEmail(email)
  }, [])

  const onSuccess = (orderNumber: string) => {
    const paymentPendingPageHref = getLinkHref({
      type: 'payment-pending',
      data: { orderNumber },
    })

    router.push(paymentPendingPageHref)
  }

  const handleStartPayment = async () => {
    if (email.trim() && !email.trim().match(/.+[@].+/gm)) {
      setEmailError('Укажите корректный email')
      return
    }

    await dispatch(changeUserEmail({ email }))

    await dispatch(
      fetchPaymentAsync({
        id: stageId,
        email: email,
        number: carNumber,
        isConsentPersonalData: !!email,
        onSuccess,
      })
    )
  }

  const handleQrCodeButtonClick = async () => {
    const applyReceiptDiscountHref = getLinkHref({
      type: 'apply-receipt-discount',
      data: { areaId, stageId },
    })
    router.push(applyReceiptDiscountHref)
  }

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleStartPayment()
    }
  }

  useLayoutEffect(() => {
    const number = getFormattedCarNumber()
    setCarNumber(number)
  }, [])

  useEffect(() => { 
    return () => {
      dispatch(resetPaymentInfo())
    }
  }, [])

  if (isNoPaymentRequired) {
    return <NoPaymentRequired area={areaInfo} paidUntil={paidUntil} />
  }

  const isScanQrCodeButtonAvailable = isRenterDiscountAvailable && +data.amount > 0

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <div>
          <Container>
            <div className={styles.header}>
              <Title className={styles.price}>
                {Math.round(stageCost.data?.total_amount || 0)} ₽
              </Title>
              {time > 0 && (
                <Text className={styles.time} isBold>
                  Длительность сессии:
                  <Time timeInSeconds={time} />
                </Text>
              )}
              {carNumber ? <CarNumberLabel number={carNumber} /> : <></>}
            </div>
          </Container>
          <Container>
            <div className={styles.content}>
              {stageCost.data && <StageCost data={stageCost.data} />}
            </div>

            <div className={styles.finesWrapper}>
              <p>Итого: </p>
              <p>{Math.round(stageCost.data?.total_amount || 0)} ₽</p>
            </div>
          </Container>

          <Container className={styles.container}>
            <Title className={styles.content__title} level={4} size={24}>
              Укажите ваш email
            </Title>

            <Text level={2} className={styles.content__paragraph}>
              Ваш email необходим, чтобы мы могли прислать чек об успешной оплате.
            </Text>
            <EmailInput
              onChange={onInputChange}
              onKeyPress={handleKeyPress}
              value={email}
              error={emailError}
            />
          </Container>
        </div>

        <Container className={styles.buttons}>
          <Button
            withLoader
            onClick={handleStartPayment}
            className={styles.button}
            loading={loading}
            background={areaInfo?.customising.button_back_color}
          >
            Оплатить
          </Button>
          {isScanQrCodeButtonAvailable && (
            <Button
              withLoader
              onClick={handleQrCodeButtonClick}
              className={styles.button}
              background={areaInfo?.customising.button_back_color}
            >
              Сканировать QR-код по чеку
            </Button>
          )}
          <Button component="a" href={supportPhoneLink} className={styles.button} isTransparent>
            Обратиться в поддержку
          </Button>
        </Container>
      </div>
    </section>
  )
}
